import React, { useMemo, useState } from 'react';
import { X, Search, Check, Forward as ForwardIcon, Loader2 } from 'lucide-react';
import { avatarColor, initials, relativeTime } from './utils';

/**
 * Forward picker modal — lets the user pick one or more chats to forward one
 * or more messages to. Relies on the sidebar `chats` list (already in state).
 * Calls `onConfirm(targetPhones)`; the parent performs the actual POSTs.
 */
const ForwardPicker = ({ open, onClose, chats, messages, onConfirm, sending }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  if (!open) return null;

  const toggle = (phone) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  };

  const q = query.trim().toLowerCase();
  const filtered = (chats || []).filter(c => !q
    || (c.recipientName || '').toLowerCase().includes(q)
    || (c.phone || '').toLowerCase().includes(q));

  const count = selected.size;
  const msgCount = (messages || []).length;

  const handleConfirm = () => {
    if (!count || sending) return;
    onConfirm(Array.from(selected));
  };

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="inbox-forward-picker"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ForwardIcon size={16} className="text-emerald-600" />
            <h3 className="font-semibold text-gray-800 text-sm">Forward {msgCount > 1 ? `${msgCount} messages` : 'message'}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="inbox-forward-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-3 pt-3 pb-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or phone"
              className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500/40 focus:bg-white"
              data-testid="inbox-forward-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-[200px]">
          {filtered.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-12">No chats match</div>
          ) : (
            <>
              <div className="px-4 pt-2 pb-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Recent chats
              </div>
              {filtered.map(c => {
                const isSel = selected.has(c.phone);
                return (
                  <button
                    key={c.phone}
                    onClick={() => toggle(c.phone)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                      ${isSel ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                    data-testid={`inbox-forward-target-${c.phone}`}
                  >
                    <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white font-semibold text-xs ${avatarColor(c.phone)}`}>
                      {initials(c.recipientName, c.phone)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{c.recipientName || c.phone}</p>
                      <p className="text-[11px] text-gray-500 truncate">{c.phone} · {relativeTime(c.lastMessageAt)}</p>
                    </div>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center border-2 transition-colors
                      ${isSel ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                      {isSel && <Check size={13} className="text-white" />}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>

        <div className="px-3 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
          <span className="text-xs text-gray-500 flex-1">
            {count === 0 ? 'Select one or more chats' : `${count} chat${count > 1 ? 's' : ''} selected`}
          </span>
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-white rounded-lg">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={!count || sending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="inbox-forward-confirm"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <ForwardIcon size={13} />}
            Forward {count > 0 ? `(${count})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForwardPicker;
